/**
 * InspectionAssigneeDialog - Edição do tipo e do responsável da vistoria.
 *
 * Reaproveita o seletor de usuários usado em Negócios e mantém as regras de
 * usuário, fila global e proprietário separadas. A persistência permanece
 * atômica na RPC update_checklist_assignee.
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-28 v1.0.0 - Criação inicial.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserSelector } from '@/components/crm/UserSelector';
import { useGlobalDistributionQueues } from '@/hooks/useChecklistTypes';

export type InspectionAssigneeMode = 'user' | 'queue' | 'owner';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: InspectionAssigneeMode;
  userId: string | null;
  queueId: string | null;
  isSaving: boolean;
  onSave: (value: { mode: InspectionAssigneeMode; userId: string | null; queueId: string | null }) => void;
}

/** Diálogo compacto no padrão de edição de pessoas dos Negócios. */
export function InspectionAssigneeDialog({ open, onOpenChange, mode, userId, queueId, isSaving, onSave }: Props) {
  const [nextMode, setNextMode] = useState<InspectionAssigneeMode>(mode);
  const [nextUserId, setNextUserId] = useState<string | null>(userId);
  const [nextQueueId, setNextQueueId] = useState<string | null>(queueId);
  const { data: queues = [] } = useGlobalDistributionQueues(open && nextMode === 'queue');

  // Reinicia o formulário com o vínculo vigente sempre que o diálogo abre.
  useEffect(() => {
    if (!open) return;
    setNextMode(mode);
    setNextUserId(userId);
    setNextQueueId(queueId);
  }, [open, mode, userId, queueId]);

  const hasChanges = nextMode !== mode
    || (nextMode === 'user' && nextUserId !== userId)
    || (nextMode === 'queue' && nextQueueId !== queueId);
  const invalid = (nextMode === 'user' && !nextUserId) || (nextMode === 'queue' && !nextQueueId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Editar responsável</DialogTitle>
          <DialogDescription>
            A troca revoga o link externo atual para proteger a identidade do novo responsável.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Tipo de responsável</Label>
            <Select value={nextMode} onValueChange={(value) => setNextMode(value as InspectionAssigneeMode)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">Usuário interno</SelectItem>
                <SelectItem value="queue">Fila global</SelectItem>
                <SelectItem value="owner">Cliente/proprietário</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {nextMode === 'user' && (
            <div className="space-y-2">
              <Label>Usuário responsável</Label>
              <UserSelector value={nextUserId} onSelect={setNextUserId} placeholder="Selecione um usuário..." allowClear={false} />
              <p className="text-xs text-muted-foreground">O e-mail deste usuário será exigido no acesso pelo link externo.</p>
            </div>
          )}

          {nextMode === 'queue' && (
            <div className="space-y-2">
              <Label>Fila global</Label>
              <Select value={nextQueueId ?? undefined} onValueChange={setNextQueueId}>
                <SelectTrigger><SelectValue placeholder="Selecione uma fila" /></SelectTrigger>
                <SelectContent>{queues.map((queue) => <SelectItem key={queue.id} value={queue.id}>{queue.name}</SelectItem>)}</SelectContent>
              </Select>
              {!queues.length && <p className="text-xs text-muted-foreground">Nenhuma fila global ativa disponível.</p>}
            </div>
          )}

          {nextMode === 'owner' && (
            <p className="text-sm text-muted-foreground">
              O proprietário congelado na vistoria será o responsável e confirmará o CPF/CNPJ para acessar o link.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>Cancelar</Button>
          <Button disabled={isSaving || invalid || !hasChanges} onClick={() => onSave({ mode: nextMode, userId: nextUserId, queueId: nextQueueId })}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar alterações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}